import { COMMA, ENTER, PERIOD } from '@angular/cdk/keycodes';
import { DatePipe, NgClass, registerLocaleData, SlicePipe } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAddressBook, faFileCirclePlus, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { AngularEditorConfig, AngularEditorModule } from '@kolkov/angular-editor';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DialogContactsData } from 'src/app/components/dialogs/contacts/models/dialog-contacts-data';
import { EmailAddressType } from 'src/app/models/enums/email-address-type';
import { Attachment } from 'src/app/models/server/attachment';
import { DraftEmail } from 'src/app/models/server/draft-email';
import { EmailAddress } from 'src/app/models/server/email-address';
import { EmailTemplate } from 'src/app/models/server/email-template';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { IEmail } from 'src/app/models/server/interface/email.interface';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { EmailAddressesService } from 'src/app/services/http/email-addresses.service';
import { EMailTemplatesService } from 'src/app/services/http/email-templates.service';
import { FoldersService } from 'src/app/services/http/folders.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';
import { environment } from 'src/environments/environment';
import { FileUploaderCustom } from '../../../core/file-uploader-custom';
import { AvmFileSizePipe } from '../../../core/pipes/avm-file-size.pipe';
import { ContactDataType } from '../contacts/models/contact-data-type';
import { DialogSendEmailData } from './models/dialog-send-email-data';

registerLocaleData(localeFr);

@Component({
    selector: 'app-send-email',
    templateUrl: './send-email.component.html',
    styleUrls: ['./send-email.component.scss'],
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        FormsModule,
        MatOptionModule,
        NgxSpinnerModule,
        MatButtonModule,
        FaIconComponent,
        FileUploadModule,
        ReactiveFormsModule,
        MatChipsModule,
        MatIconModule,
        MatInputModule,
        NgClass,
        AngularEditorModule,
        SlicePipe,
        AvmFileSizePipe,
    ],
})
export class SendEmailComponent implements OnInit, OnDestroy {
    /**
     * Références des icônes utilisés dans le template.
     */
    addressbookIcon = faAddressBook;
    attachmentIcon = faPaperclip;
    internalFileIcon = faFileCirclePlus;

    /**
     * Références des Enums utilisées dans le template.
     */
    public AddressType = EmailAddressType;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * URL pour le webService d'upload.
     */
    get baseUrl() {
        if (environment.production) {
            return '';
        } else {
            return environment.serverUrl;
        }
    }

    /**
     * Liste des modèles de mails de l'organisation.
     */
    public templates: EmailTemplate[] = [];

    /**
     * Liste des adresses mail d'émission de l'organisation.
     */
    public senderAddresses: EmailAddress[] = [];

    /**
     * Identifiant du modèle de mail sélectionné (0 indique aucun modèle de mail sélectionné).
     */
    public selectedTemplateId: string = '0';

    /**
     * Instance de l'email en cours d'edition.
     */
    public email: IEmail;

    /**
     * Expression régulière utilisée pour valider le format d'une adresse e-mail.
     */
    public emailPattern = new RegExp(
        /^("?)((\b[\p{L}\p{N}\-_]+\b)\s?)+("?)\s?<([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)>|([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z_-]+)$/u
    );

    /**
     * Valeur indiquant si le format de l'adresse 'To' est valide.
     */
    public isToAddressFormatValid: boolean = true;

    /**
     * Valeur indiquant si le format de l'adresse 'Cc' est valide.
     */
    public isCcAddressFormatValid: boolean = true;

    /**
     * Valeur indiquant si le format de l'adresse 'Cci' est valide.
     */
    public isCciAddressFormatValid: boolean = true;

    /**
     * Champs du formulaire d'envoi de mail.
     */
    public subjectFormControl = new UntypedFormControl(null, [Validators.required]);
    public bodyFormControl = new UntypedFormControl(null, [Validators.required]);
    public senderAddressFormControl = new UntypedFormControl(null);
    public addressToFormControl = new UntypedFormControl(null, [Validators.required]);
    public addressCcFormControl = new UntypedFormControl(null);
    public addressCciFormControl = new UntypedFormControl(null);
    public addressToInputFormControl = new UntypedFormControl(null);
    public addressCcInputFormControl = new UntypedFormControl(null);
    public addressCciInputFormControl = new UntypedFormControl(null);

    /**
     * Touches utilisées pour valider la saisie d'une adresse e-mail.
     * PERIOD: point-virgule
     */
    readonly separatorKeysCodes: number[] = [ENTER, COMMA, PERIOD];

    /**
     * Configuration de l'editeur de texte utilisé pour saisir le corps de l'e-mail.
     */
    config: AngularEditorConfig = {
        editable: true,
        spellcheck: true,
        height: 'auto',
        placeholder: 'Ecrire votre message ici ...',
        translate: 'no',
        defaultParagraphSeparator: 'DIV',
        defaultFontName: 'Arial',
        toolbarHiddenButtons: [['insertVideo']],
        customClasses: [
            {
                name: 'quote',
                class: 'quote',
            },
            {
                name: 'redText',
                class: 'redText',
            },
            {
                name: 'titleText',
                class: 'titleText',
                tag: 'h1',
            },
        ],
    };

    /**
     * Composant d'upload de fichiers.
     */
    public uploader: FileUploaderCustom = null;

    /**
     * Liste des fichiers du dossier non sélectionnés
     */
    private folderFilesNotSelected: Attachment[] = [];

    /**
     * Liste des fichiers du dossiers sélectionnés
     */
    public folderFilesSelected: Attachment[] = [];

    /**
     * Valeur indiquant si l'utilisateur est en train de sauver le mail en tant que brouillon (true) ou de l'envoyer (false)
     */
    private isDraftEmailSave: boolean = false;

    /**
     * Constructeur.
     * @param connectedUserStateService
     * @param mailtemplateService
     * @param dialogService
     * @param dialogRef
     * @param data
     */
    constructor(
        private connectedUserStateService: ConnectedUsersStateService,
        private viewsStateService: ViewsStateService,
        private mailtemplateService: EMailTemplatesService,
        private emailAddressesService: EmailAddressesService,
        public folderService: FoldersService,
        private dialogService: DialogService,
        private spinner: SpinnerService,
        public dialogRef: MatDialogRef<SendEmailComponent>,
        private datePipe: DatePipe,
        @Inject(MAT_DIALOG_DATA) public data: DialogSendEmailData
    ) {}

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.email = {
            fromAddressId: -1,
            to: [],
            cc: [],
            cci: [],
            subject: '',
            body: '',
            folderFiles: [],
        };

        this.loadTemplates();

        this.loadSenderAddresses();

        this.loadFolderFiles();

        this.InitUploadComponent();

        this.addressToFormControl.valueChanges.subscribe((v) => {
            if (this.isToAddressFormatValid === false) {
                this.addressToFormControl.setErrors({ invalidFormat: true });
            }
        });

        this.addressCcFormControl.valueChanges.subscribe((v) => {
            if (this.isCcAddressFormatValid === false) {
                this.addressCcFormControl.setErrors({ invalidFormat: true });
            }
        });

        this.addressCciFormControl.valueChanges.subscribe((v) => {
            if (this.isCciAddressFormatValid === false) {
                this.addressCciFormControl.setErrors({ invalidFormat: true });
            }
        });

        if (this.isMailResponse()) {
            this.initMail(this.data.document, true);
        }

        if (this.isMailResend()) {
            this.initMail(this.data.document, false);
        }
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelée lorsque l'utilisateur valide la saisie d'une adresse e-mail avec la touche ENTREE, virgule, point-virgule ou en quittant le champ de saisie.
     * @param event : Événement émis par le composant de saisie d'adresse e-mail.
     * @param AddressType : Type d'adresse.
     */
    public addAddress(event: MatChipInputEvent, addressType: EmailAddressType): void {
        const input = event.chipInput.inputElement;
        const value = event.value.trim();

        if (value !== '') {
            if (!!value && this.emailPattern.test(value) === true) {
                input.value = '';
                switch (addressType) {
                    case EmailAddressType.to:
                        this.email.to.push(value);
                        this.isToAddressFormatValid = true;
                        this.addressToFormControl.markAsPristine();
                        this.addressToFormControl.markAsUntouched();
                        break;
                    case EmailAddressType.cc:
                        this.email.cc.push(value);
                        this.isCcAddressFormatValid = true;
                        this.addressCcFormControl.markAsPristine();
                        this.addressCcFormControl.markAsUntouched();
                        break;
                    case EmailAddressType.cci:
                        this.email.cci.push(value);
                        this.isCciAddressFormatValid = true;
                        this.addressCciFormControl.markAsPristine();
                        this.addressCciFormControl.markAsUntouched();
                        break;
                }
            } else {
                switch (addressType) {
                    case EmailAddressType.to:
                        this.isToAddressFormatValid = false;
                        break;
                    case EmailAddressType.cc:
                        this.isCcAddressFormatValid = false;
                        break;
                    case EmailAddressType.cci:
                        this.isCciAddressFormatValid = false;
                        break;
                }
            }
        } else {
            switch (addressType) {
                case EmailAddressType.to:
                    this.isToAddressFormatValid = true;
                    break;
                case EmailAddressType.cc:
                    this.isCcAddressFormatValid = true;
                    break;
                case EmailAddressType.cci:
                    this.isCciAddressFormatValid = true;
                    break;
            }
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'icône de suppression d'une adresse e-mail.
     * @param address : adresse e-mail à supprimer.
     * @param addressType : Type d'adresse.
     */
    public removeAddress(address: string, addressType: EmailAddressType): void {
        switch (addressType) {
            case EmailAddressType.to:
                this.email.to.splice(this.email.to.indexOf(address), 1);

                if (this.email.to.length == 0) {
                    // Necessaire pour déclencher le validateur Requiered.
                    this.addressToFormControl.setValue('');
                }
                break;
            case EmailAddressType.cc:
                this.email.cc.splice(this.email.cc.indexOf(address), 1);
                break;
            case EmailAddressType.cci:
                this.email.cci.splice(this.email.cci.indexOf(address), 1);
                break;
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'icône des contacts.
     */
    public displayContacts(): void {
        this.dialogService
            .openContacts(ContactDataType.Email)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x: DialogContactsData) => {
                if (!!x) {
                    if (this.email.to.length == 0) {
                        this.isToAddressFormatValid = true;
                    }

                    x.toAddresses.forEach((to) => {
                        this.email.to.push(to);
                    });

                    x.ccAddresses.forEach((cc) => {
                        this.email.cc.push(cc);
                    });

                    x.cciAddresses.forEach((cci) => {
                        this.email.cci.push(cci);
                    });
                }
            });
    }

    /**
     * Appelée lorsque l'utilisateur sélectionne un modèle d'e-mail.
     * @param templateId : Identifiant du modèle d'e-mail sélectionné.
     */
    public applyTemplate(templateId: number): void {
        if (templateId != 0) {
            this.spinner.show('send-email-spinner');
            this.mailtemplateService.get(templateId, this.data.folderId).subscribe((x) => {
                if (this.data.isResponse === false) {
                    this.overwriteMailFields(x);
                } else {
                    this.completeMailFields(x);
                }
            });
        } else {
            this.email.subject = '';
            this.subjectFormControl.setValue(this.email.subject);
            this.email.body = '';
            this.bodyFormControl.setValue(this.email.body);
            this.email.fromAddressId = -1;
            this.senderAddressFormControl.setValue(this.email.fromAddressId);
            this.senderAddressFormControl.markAsDirty();
            this.senderAddressFormControl.markAsTouched();
            this.senderAddressFormControl.updateValueAndValidity();
            this.email.to = [];
            this.addressToFormControl.setValue(this.email.to);
            this.addressToFormControl.markAsDirty();
            this.addressToFormControl.markAsTouched();
            this.addressToFormControl.updateValueAndValidity();
            this.email.cc = [];
            this.email.cci = [];
        }
    }

    private overwriteMailFields(x: EmailTemplate) {
        const emailTemplate = x;

        if (emailTemplate.template.fromAddressId === null) {
            this.email.fromAddressId = -1;
        } else {
            this.email.fromAddressId = emailTemplate.template.fromAddressId;
        }
        this.senderAddressFormControl.setValue(this.email.fromAddressId);

        this.email.to = [];
        this.addressToInputFormControl.setValue('');

        if (!!emailTemplate.template.to) {
            emailTemplate.template.to.forEach((to) => {
                this.email.to.push(to);
            });

            this.addressToFormControl.setValue(this.email.to);
        }
        this.addressToFormControl.markAsDirty();
        this.addressToFormControl.markAsTouched();
        this.addressToFormControl.setErrors(null);
        this.addressToFormControl.updateValueAndValidity();

        this.email.cc = [];
        this.addressCcInputFormControl.setValue('');

        if (!!emailTemplate.template.cc) {
            emailTemplate.template.cc.forEach((cc) => {
                this.email.cc.push(cc);
            });
        }
        this.addressCcFormControl.markAsTouched();
        this.addressCcFormControl.setErrors(null);

        this.email.cci = [];
        this.addressCciInputFormControl.setValue('');

        if (!!emailTemplate.template.cci) {
            emailTemplate.template.cci.forEach((cci) => {
                this.email.cci.push(cci);
            });
        }
        this.addressCciFormControl.markAsTouched();
        this.addressCciFormControl.setErrors(null);

        this.email.subject = emailTemplate.template.subject;
        this.subjectFormControl.setValue(this.email.subject);
        this.subjectFormControl.markAsTouched();
        this.subjectFormControl.updateValueAndValidity();
        this.email.body = emailTemplate.template.body;
        this.bodyFormControl.setValue(this.email.body);
        this.bodyFormControl.markAsTouched();
        this.bodyFormControl.updateValueAndValidity();

        this.uploader.clearQueue();

        if (emailTemplate.template.predefinedAttachments && emailTemplate.template.predefinedAttachments.length > 0) {
            emailTemplate.template.predefinedAttachments.forEach((x) => {
                this.mailtemplateService.getAttachmentFile(emailTemplate.id, x.id, x.name).subscribe((y) => {
                    let attachmentFile = new File([y], x.name, {
                        type: y.type,
                    });
                    this.uploader.addToQueue([attachmentFile]);
                    this.spinner.hide('send-email-spinner');
                });
            });
        } else {
            this.spinner.hide('send-email-spinner');
        }
    }

    private completeMailFields(x: EmailTemplate) {
        const emailTemplate = x;

        if (emailTemplate.template.subject) {
            // Si un sujet est spécifié dans le modèle de mail, on surcharge le sujet de la réponse.
            this.email.subject = emailTemplate.template.subject;
            this.subjectFormControl.setValue(this.email.subject);
            this.subjectFormControl.markAsTouched();
            this.subjectFormControl.updateValueAndValidity();
        }

        if (emailTemplate.template.body) {
            // Si un corps est spécifié dans le modèle de mail, on précede le corps de la réponse par le texte contenu dans le corps du modèle de mail.
            this.email.body = `${emailTemplate.template.body}<br>${this.email.body}`;
            this.bodyFormControl.setValue(this.email.body);
            this.bodyFormControl.markAsTouched();
            this.bodyFormControl.updateValueAndValidity();
        }

        if (emailTemplate.template.predefinedAttachments && emailTemplate.template.predefinedAttachments.length > 0) {
            // Si le modèle de mail contient des pièces jointes prédéfinies, on les ajoute à la liste des pièces jointes de la réponse.
            emailTemplate.template.predefinedAttachments.forEach((x) => {
                this.mailtemplateService.getAttachmentFile(emailTemplate.id, x.id, x.name).subscribe((y) => {
                    let attachmentFile = new File([y], x.name, {
                        type: y.type,
                    });
                    this.uploader.addToQueue([attachmentFile]);
                    this.spinner.hide('send-email-spinner');
                });
            });
        } else {
            this.spinner.hide('send-email-spinner');
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Enregistrer le brouillon'.
     */
    public save(): void {
        this.spinner.show('send-email-spinner');
        this.fillData();

        // Tricks : L'envoi de l'email se fait via le formulaire de la requête multipart (les propriétés de l'object Email sont envoyées sous forme de clé / valeur ou la valeur ne peut etre qu'un type primitif). Il n'est donc pas possible de transmettre un tableau d'Attachment. Du coup on convertit le tableau d'Attachment en JSON et on passe le texte JSON comme valeur. Pour forcer le texte JSON dans la propriété de type Attachment on la cast en any.
        this.email['folderFiles'] = JSON.stringify(this.folderFilesSelected) as any;

        const user = this.connectedUserStateService.getUserConnected();

        this.isDraftEmailSave = true;
        this.uploader.options.method = 'POST';
        this.uploader.options.url = `${this.baseUrl}/api/organizations/${user.organization.id}/views/${this.data.folderViewId}/folders/${this.data.folderId}/draft-emails`;

        this.uploader.uploadAllFiles(this.email);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Valider'.
     */
    public send(): void {
        if (
            this.email.to.length > 0 &&
            this.subjectFormControl.valid &&
            this.bodyFormControl.valid &&
            this.isToAddressFormatValid &&
            this.isCcAddressFormatValid &&
            this.isCciAddressFormatValid
        ) {
            this.spinner.show('send-email-spinner');

            this.fillData();

            this.email.folderFiles = JSON.stringify(this.folderFilesSelected) as any;

            const user = this.connectedUserStateService.getUserConnected();

            this.isDraftEmailSave = false;
            this.uploader.options.method = 'POST';
            this.uploader.options.url = `${this.baseUrl}/api/organizations/${user.organization.id}/views/${this.data.folderViewId}/folders/${this.data.folderId}/email`;

            this.uploader.uploadAllFiles(this.email);
        } else {
            this.addressToFormControl.markAsTouched();
            this.addressToFormControl.updateValueAndValidity();
            this.addressCcFormControl.markAsTouched();
            this.addressCciFormControl.markAsTouched();
            this.subjectFormControl.markAsTouched();
            this.bodyFormControl.markAsTouched();
        }
    }

    /**
     * Adresses emails à ajouter lors d'un copier/coller
     * @param event copier/coller
     */
    addressesToPaste(event: ClipboardEvent, addressType: EmailAddressType): void {
        event.preventDefault();
        event.clipboardData
            .getData('Text')
            .split(/;|,|\n/)
            .forEach((value) => {
                if (value.trim() && this.emailPattern.test(value.trim()) === true) {
                    const mail = this.emailPattern.exec(value.trim())[0];
                    switch (addressType) {
                        case EmailAddressType.to:
                            this.email.to.push(mail);
                            this.isToAddressFormatValid = true;
                            break;
                        case EmailAddressType.cc:
                            this.email.cc.push(mail);
                            this.isCcAddressFormatValid = true;
                            break;
                        case EmailAddressType.cci:
                            this.email.cci.push(mail);
                            this.isCciAddressFormatValid = true;
                            break;
                    }
                }
            });
    }

    /**
     * Initialise le composant d'upload de fichiers.
     */
    private InitUploadComponent() {
        const user = this.connectedUserStateService.getUserConnected();
        this.uploader = new FileUploaderCustom({
            url: `${this.baseUrl}/api/organizations/${user.organization.id}/views/${this.data.folderViewId}/folders/${this.data.folderId}/email`,
            authTokenHeader: 'Authorization',
            authToken: `Bearer ${user.token.accessToken}`,
        });

        this.uploader.onSuccessItem = (fileItem, response) => {
            if (this.isDraftEmailSave === false) {
                let document = FolderDocument.fromJson(JSON.parse(response));
                this.spinner.hide('send-email-spinner');
                this.dialogRef.close(document);
            } else {
                let draft = DraftEmail.from(JSON.parse(response));
                this.spinner.hide('send-email-spinner');
                this.dialogRef.close(draft);
            }
        };
    }

    /**
     * Charge les modèles d'e-mails en mémoire.
     */
    private loadTemplates() {
        this.spinner.show('email-template-spinner');
        this.mailtemplateService
            .List(this.data.folderViewId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.templates = x;
                this.spinner.hide('email-template-spinner');
            });
    }

    /**
     * Charge les modèles d'e-mails en mémoire.
     */
    private loadSenderAddresses() {
        this.emailAddressesService
            .ListSendingEmailAddress()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.senderAddresses = x;
            });
    }

    /**
     * Charge les fichiers d'un dossier.
     */
    private loadFolderFiles() {
        this.folderService.getFolderFiles(this.data.folderId).subscribe((x) => {
            // Exclu les corps des mails
            this.folderFilesNotSelected = x.filter((x) => x.name !== 'body.txt');
        });
    }

    private fillData() {
        this.email.fromAddressId = this.senderAddressFormControl.value !== -1 ? this.senderAddressFormControl.value : null;
        this.email.subject = this.subjectFormControl.value;

        if (this.isMailResponse()) {
            // On ajoute le style pour afficher l'encadré contenant les infos De, à, sujet
            this.email.body = `<html><head><style>p.MsoNormal {margin:0cm;font-size:11.0pt;font-family:"Calibri",sans-serif;} div.separator {border:none;border-top:solid #E1E1E1 1.0pt;padding:3.0pt 0cm 0cm 0cm;}</style></head><body>${this.bodyFormControl.value}</body></html>`;
        } else {
            this.email.body = this.bodyFormControl.value;
        }
    }

    /**
     * Appelée à la réponse d'un mail.
     * @param document : e-mail d'origine.
     * @param isResponse : Indique si le mail est une réponse à un mail existant (true si c'est une réponse, false si c'est renvoi de mail).
     */
    public initMail(document: FolderDocument, isResponse: boolean): void {
        // Récupération du mail d'origine
        this.spinner.show('send-email-spinner');
        const mailAttachment = document.attachments.find((x) => x.name == 'body.txt' || x.name == 'Corps du mail');
        this.folderService
            .download(this.data.folderViewId, this.data.folderId, this.data.document.id, mailAttachment.id)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((blob) => {
                blob.text().then((text) => {
                    // Contenu de l'email

                    this.email.fromAddressId = isResponse
                        ? -1
                        : this.senderAddresses.findIndex((x) => document.from.includes(x.address)) !== -1
                          ? this.senderAddresses.find((x) => document.from.includes(x.address)).id
                          : -1;
                    this.senderAddressFormControl.setValue(this.email.fromAddressId);

                    this.email.to = isResponse ? [document.from] : [document.to];
                    this.addressToFormControl.setValue(this.email.to);

                    this.email.subject = isResponse ? `RE : ${document.subject}` : document.subject;
                    this.subjectFormControl.setValue(this.email.subject);

                    if (isResponse) {
                        const emailDate = this.datePipe.transform(this.data.document.creationDate, 'EEEE d MMMM y HH:mm', undefined, 'fr') || '';

                        this.email.body = `<html><head><style>p.MsoNormal {margin:0cm;font-size:11.0pt;font-family:"Calibri",sans-serif;} div.separator {border:none;border-top:solid #E1E1E1 1.0pt;padding:3.0pt 0cm 0cm 0cm;}</style></head><body><br><br><div class="separator"><p class="MsoNormal"><b>De&nbsp;:</b> ${this.sanitize(this.data.document.from)} <br><b>Envoyé&nbsp;:</b> ${emailDate}<br><b>À&nbsp;:</b> ${this.sanitize(this.data.document.to)}<br><b>Objet&nbsp;:</b> ${this.sanitize(this.data.document.subject)}</p></div><br><p class="MsoNormal">${text}</p></body></html>`;
                    } else {
                        this.email.body = text;
                    }

                    this.bodyFormControl.setValue(this.email.body);
                    this.spinner.hide('send-email-spinner');
                });
            });
    }

    /**
     * Lors du click pour ajouter un fichier du dossier.
     */
    public internalFileToAdd(): void {
        this.dialogService
            .openSelectFolderFiles(this.folderFilesNotSelected)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x: Attachment[]) => {
                if (x) {
                    this.folderFilesSelected.push(...x);

                    this.folderFilesSelected.forEach((a) => {
                        this.folderFilesNotSelected.splice(
                            this.folderFilesNotSelected.findIndex((b) => b.id === a.id),
                            1
                        );
                    });
                }
            });
    }

    /**
     * Lors de la suppression d'un fichier de dossier.
     * @param attachment fichier de dossier à supprimer.
     */
    public deleteFolderFile(attachment: Attachment) {
        this.folderFilesSelected.splice(
            this.folderFilesSelected.findIndex((a) => a.id === attachment.id),
            1
        );
        this.folderFilesNotSelected.push(attachment);
    }

    /**
     * Retourne une valeur indiquant si la boite de dialogue affiche une réponse à un mail.
     * @returns True, si la boite de dialogue affiche une réponse à un mail; false autrement.
     */
    public isMailResponse(): boolean {
        return this.data.document !== null && this.data.isResponse === true;
    }

    /**
     * Retourne une valeur indiquant si la boite de dialogue affiche une réponse à un mail.
     * @returns True, si la boite de dialogue affiche une réponse à un mail; false autrement.
     */
    public isMailResend(): boolean {
        return this.data.document !== null && this.data.isResponse === false;
    }

    /**
     * Nettoie un texte pour remplacer les caractères < et > pour qu'il ne soient pas interprétés par le navigateur comme * balises HTML.
     * @param text : texte à nettoyer.
     * @returns : Texte néttoyé.
     */
    private sanitize(text: string): string {
        return text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
}
